import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import RlNavAccordionItem from "./RlNavAccordionItem";
import RlNavContent from "./RlNavContent";
import RlNavContentItem from "./RlNavContentItem";
import RlNavHeaderItem from "./RlNavHeaderItem";
import RlNavItem from "./RlNavItem";
import RlNavItems from "./RlNavItems";

export const RlNavContext = React.createContext({
  activeKey: 0,
  setActiveKey: () => null,
  onToggleClick: () => null,
  expanded: true,
  setExpanded: expanded => undefined
});
/**
 * This component is made to replace the old side navigation from Sample summary.
 * It can be used in two different ways:
 *  1. Using something like React Router
 *  2. Using "navKey"
 * Example usage with React Router (Link & Outlet components are from the react-router-dom lib):

    //Root.tsx
    <Route element={Nav} path="/basepath">
      <Route path="somepath" element={ChildElementRenderedInOutlet}>
      <Route path="otherpath" element={OtherChildElementRenderedInOutlet}>
     </Route>

     // Nav.tsx
     <RlNav>
       <RlNav.Items>
         <RlNav.Item>
           <Link to="/somepath">
             <div>Click to show content</div>
           </Link>
            <Link to="/otherpath">
             <div>Click to show other content</div>
           </Link>
          </RlNav.Item>
        </RlNav.Items>
        <RlNav.Content>
            <Outlet />
        </RlNav.Content>
     </RlNav>

 * Example usage with navKey (Can be found in RlNavDemo page also):
 *
 *   <RlNav defaultNavKey={key1}>
      <RlNav.Items>
        <RlNav.Item navKey={key1}>
          <div>Click to show content</div>
         </RlNav.Item>
        <ComponentWithItems />   // inside this component RLNav.Item (RlNavItem) can be used as well
      </RlNav.Items>
      <RlNav.Content>
        <RlNav.ContentItem navKey={key1}>
          <ComponentWithContent />
        <RlNav.ContentItem>
        <RlNav.ContentItem navKey={key2}>
          <SomeOtherComponent />
        <RlNav.ContentItem>
      </RlNav.Content>
    </RlNav>

 * <RlNav> is composed of few other components (* indicates required ones):
 *  - <RlNav.Items>* :     Here goes everything that we want to show in the sidebar
 *  - <RlNav.Item>   :     Wrapper which accepts navKey as prop. When anything it wraps is clicked on, RlNav.ContentItem
 *                         with the matching navKey inside RlNav.Content will be shown.
 *                         This component should be placed inside RlNav.Items. It can also be used inside any component
 *                         that is child of RlNav.Items (it can be used as RlNavItem in that case).
 *  - <RlNav.Content>*:    Here goes the content which will be shown on the right side of the sidebar.
 *                         You should put RlNav.ContentItem inside this component.
 *                         When RlNav.Item with same navKey as RlNavContentItem is clicked, that content will be
 *                         displayed. You can also use it as in example 1 with React Router.
 * - <RlNav.ContentItem>: Wraps components in the RlNav.Content. Must have matching navKey as one of RlNav.Item components.
 *                        Needed only when navKey is used like in example 2.
 */
const RlNav = ({ defaultNavKey, currentNavKey, children }) => {
  const [expanded, setExpanded] = useState(true);
  const [activeKey, setActiveKey] = useState(defaultNavKey);

  useEffect(() => {
    setActiveKey(currentNavKey);
  }, [currentNavKey]);

  const getChildComponent = component =>
    children.find(({ type }) => type === component);

  const toggleExpand = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const contextValue = useMemo(
    () => ({ activeKey, setActiveKey, toggleExpand, expanded, setExpanded }),
    [activeKey, toggleExpand, expanded]
  );
  return (
    <RlNavContext.Provider value={contextValue}>
      <div className={expanded ? "sidebar-expanded" : "sidebar-collapsed"}>
        <div id="content-wrap">
          {getChildComponent(RlNavItems)}
          {getChildComponent(RlNavContent)}
        </div>
      </div>
    </RlNavContext.Provider>
  );
};
RlNav.propTypes = {
  defaultNavKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentNavKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired
};
RlNav.Item = RlNavItem;
RlNav.Items = RlNavItems;
RlNav.Content = RlNavContent;
RlNav.ContentItem = RlNavContentItem;
RlNav.HeaderItem = RlNavHeaderItem;
RlNav.AccordionItem = RlNavAccordionItem;
export default RlNav;
